import React, { useState, useEffect } from 'react';
import { authAPI, settingsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/helpers';
import toast from 'react-hot-toast';

const PLAN_MONTHS = { Monthly: 1, Quarterly: 3, Yearly: 12 };

function PricingPreview({ admissionFee, monthlyFee }) {
  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-xl space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment Preview</p>
      {['Monthly', 'Quarterly', 'Yearly'].map(plan => {
        const months = PLAN_MONTHS[plan];
        const planFee = monthlyFee * months;
        const firstTime = admissionFee + planFee;
        return (
          <div key={plan} className="bg-white rounded-lg p-3 border border-gray-100">
            <p className="text-sm font-semibold text-gray-700 mb-2">{plan} Plan</p>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <span className="text-gray-500">Plan fee ({months} month{months > 1 ? 's' : ''}):</span>
              <span className="font-medium text-gray-700 text-right">{formatCurrency(planFee)}</span>
              <span className="text-gray-500">First-time total (+ admission):</span>
              <span className="font-semibold text-blue-600 text-right">{formatCurrency(firstTime)}</span>
              <span className="text-gray-500">Renewal amount:</span>
              <span className="font-semibold text-green-600 text-right">{formatCurrency(planFee)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Settings() {
  const { owner, refreshOwner, logout } = useAuth();

  // Profile
  const [profileForm, setProfileForm] = useState({ name: owner?.name || '', phone: owner?.phone || '' });
  const [profileLoading, setProfileLoading] = useState(false);

  // Password
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passLoading, setPassLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Pricing
  const [pricing, setPricing] = useState({ admissionFee: 2500, monthlyFee: 2000 });
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({ admissionFee: 2500, monthlyFee: 2000 });

  useEffect(() => {
    settingsAPI.getPricing().then(res => {
      setPricing(res.data);
      setPricingDraft({ admissionFee: res.data.admissionFee, monthlyFee: res.data.monthlyFee });
    }).catch(() => {});
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileForm.name || !profileForm.phone) { toast.error('Name and phone are required'); return; }
    setProfileLoading(true);
    try {
      await authAPI.updateProfile(profileForm);
      await refreshOwner();
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setProfileLoading(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) { toast.error('New passwords do not match'); return; }
    if (passForm.newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPassLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: passForm.currentPassword, newPassword: passForm.newPassword });
      toast.success('Password changed!');
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Change failed');
    } finally { setPassLoading(false); }
  };

  const handlePricingSubmit = async (e) => {
    e.preventDefault();
    const admFee = parseFloat(pricingDraft.admissionFee);
    const monFee = parseFloat(pricingDraft.monthlyFee);
    if (!admFee || !monFee || admFee < 0 || monFee < 0) { toast.error('Enter valid fee amounts'); return; }
    setPricingLoading(true);
    try {
      const res = await settingsAPI.updatePricing({ admissionFee: admFee, monthlyFee: monFee });
      setPricing(res.data);
      toast.success('Pricing updated!');
    } catch {
      toast.error('Failed to update pricing');
    } finally { setPricingLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
        <p className="text-gray-500 text-sm mt-1">Manage account credentials and gym pricing</p>
      </div>

      {/* Owner Info */}
      <div className="card bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center text-3xl font-bold">
            {owner?.name?.charAt(0)?.toUpperCase() || 'O'}
          </div>
          <div>
            <p className="text-lg font-bold">{owner?.name}</p>
            <p className="text-blue-100 text-sm">📱 {owner?.phone}</p>
            <span className="inline-block mt-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">Gym Owner</span>
          </div>
        </div>
      </div>

      {/* ── PRICING SECTION ── */}
      <div className="card border-2 border-blue-100">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center text-lg">💰</div>
          <div>
            <h3 className="font-semibold text-gray-800">Gym Pricing</h3>
            <p className="text-xs text-gray-500">Set admission fee and monthly membership fee</p>
          </div>
        </div>

        <form onSubmit={handlePricingSubmit} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Admission Fee (₹)
                <span className="ml-1 text-xs font-normal text-gray-400">— charged once on first join</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                <input
                  className="input pl-7"
                  type="number"
                  min="0"
                  value={pricingDraft.admissionFee}
                  onChange={e => setPricingDraft(d => ({ ...d, admissionFee: e.target.value }))}
                  placeholder="2500"
                />
              </div>
            </div>
            <div>
              <label className="label">Monthly Fee (₹)
                <span className="ml-1 text-xs font-normal text-gray-400">— base per month</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">₹</span>
                <input
                  className="input pl-7"
                  type="number"
                  min="0"
                  value={pricingDraft.monthlyFee}
                  onChange={e => setPricingDraft(d => ({ ...d, monthlyFee: e.target.value }))}
                  placeholder="2000"
                />
              </div>
            </div>
          </div>

          <PricingPreview
            admissionFee={parseFloat(pricingDraft.admissionFee) || 0}
            monthlyFee={parseFloat(pricingDraft.monthlyFee) || 0}
          />

          <div className="flex justify-end">
            <button type="submit" disabled={pricingLoading} className="btn-primary min-w-[140px]">
              {pricingLoading ? 'Saving...' : 'Save Pricing'}
            </button>
          </div>
        </form>
      </div>

      {/* Update Profile */}
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1">Update Profile</h3>
        <p className="text-sm text-gray-500 mb-5">Change your display name or login phone number</p>
        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label className="label">Display Name</label>
            <input className="input" value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" />
          </div>
          <div>
            <label className="label">Phone Number (used for login)</label>
            <input className="input" type="tel" value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} placeholder="10-digit phone" />
            <p className="text-xs text-gray-400 mt-1">Changing this means you must use the new number to log in next time.</p>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={profileLoading} className="btn-primary min-w-[140px]">
              {profileLoading ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="card">
        <h3 className="font-semibold text-gray-800 mb-1">Change Password</h3>
        <p className="text-sm text-gray-500 mb-5">Use a strong password with letters and numbers</p>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <div className="relative">
              <input type={showCurrent ? 'text' : 'password'} className="input pr-16"
                value={passForm.currentPassword} onChange={e => setPassForm(f => ({ ...f, currentPassword: e.target.value }))} placeholder="Current password" />
              <button type="button" onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600">{showCurrent ? 'Hide' : 'Show'}</button>
            </div>
          </div>
          <div>
            <label className="label">New Password</label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} className="input pr-16"
                value={passForm.newPassword} onChange={e => setPassForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="Min. 6 characters" />
              <button type="button" onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600">{showNew ? 'Hide' : 'Show'}</button>
            </div>
          </div>
          <div>
            <label className="label">Confirm New Password</label>
            <input type="password"
              className={`input ${passForm.confirmPassword && passForm.confirmPassword !== passForm.newPassword ? 'border-red-400 focus:ring-red-400' : ''}`}
              value={passForm.confirmPassword} onChange={e => setPassForm(f => ({ ...f, confirmPassword: e.target.value }))} placeholder="Re-enter new password" />
            {passForm.confirmPassword && passForm.confirmPassword !== passForm.newPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>
          {passForm.newPassword && (
            <div>
              <div className="flex gap-1">
                {[1,2,3,4].map(i => (
                  <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${passForm.newPassword.length >= i * 3 ? i <= 1 ? 'bg-red-400' : i <= 2 ? 'bg-yellow-400' : i <= 3 ? 'bg-blue-400' : 'bg-green-500' : 'bg-gray-200'}`} />
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">{passForm.newPassword.length < 6 ? 'Too short' : passForm.newPassword.length < 9 ? 'Moderate' : passForm.newPassword.length < 12 ? 'Good' : 'Strong'}</p>
            </div>
          )}
          <div className="flex justify-end">
            <button type="submit" disabled={passLoading} className="btn-primary min-w-[160px]">
              {passLoading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>

      {/* Danger Zone */}
      <div className="card border border-red-100">
        <h3 className="font-semibold text-red-700 mb-1">Danger Zone</h3>
        <p className="text-sm text-gray-500 mb-4">This will log you out of the current session.</p>
        <button onClick={logout} className="btn-danger text-sm">Logout from GymPro</button>
      </div>
    </div>
  );
}

export default Settings;
